/* istanbul ignore file */ // ignore this file for component test as we cover it in selenium test
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { compose, defaults, omit } from 'underscore';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { withScope, QueryParam } from '@bingads-webui-react/scope-service';
import { withSiteMap, siteMapPropType } from '@bingads-webui-campaign-react/with-site-map';
import {
  getFilteredCampaignTypeFromRawFilter,
} from '@bingads-webui-campaign-react/shell-ui-common';
import { withGlobalFilter } from '@bingads-webui-campaign-react/global-filter-context';
import { WaterfallFilterType } from '@bingads-webui-campaign/filter-storage';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { SummaryBar } from './summary-bar';
import {
  convertToSummaryBarCampaign,
  convertToSummaryBarAdGroup,
  convertToSummaryBarExperimentCampaign,
} from './helpers/entity-converter';
import {
  WithCampaignRepository,
  WithAdGroupRepository,
  WithExperimentRepository,
  WithBulkUpdateRepository,
  WithPatchExperimentRepository,
  WithOptimizationScoreRepository,
} from './repository';

class UpdatableSummaryBar extends React.Component {
  constructor(props) {
    super(props);

    this.campaignSummarybarPerfMarker = props.appPerfMarker.createChild('Campaigns_Summary_Bar');
    this.markAction('willInit');

    this.summaryBarActivityLogger = props.instrumentation.summaryBarScenario.create();

    this.previousCampaign = null;
    this.previousAdGroup = null;
  }

  state = {
    patchAdGroup: null,
    patchCampaign: null,
  };

  shouldComponentUpdate(nextProps, nextState) {
    const skipUpdateAdGroup = nextState.patchAdGroup !== this.state.patchAdGroup && nextState.patchAdGroup === null;
    const skipUpdateCampaign = nextState.patchCampaign !== this.state.patchCampaign && nextState.patchCampaign === null;

    if (skipUpdateAdGroup || skipUpdateCampaign) {
      return false;
    }
    return !(_.isEqual(nextState, this.state) && _.isEqual(nextProps, this.props));
  }

  get getFinalProps() {
    const campaignType
      = getFilteredCampaignTypeFromRawFilter(this.props[WaterfallFilterType.CampaignType]);
    const props = {
      ...this.props,
      updateAdGroup: this.updateAdGroup,
      updateCampaign: this.updateCampaign,
      updateExperimentCampaign: this.updateExperimentCampaign,
      campaignType,
    };

    return props;
  }

  get getCampaignLoadFallback() {
    return (
      <SummaryBar
        {...this.getFinalProps}
        campaign={this.previousCampaign}
        adGroup={this.previousAdGroup}
        summaryBarActivityLogger={this.summaryBarActivityLogger}
      />
    );
  }

  markAction = (action) => {
    if (this.campaignSummarybarPerfMarker && action) {
      this.campaignSummarybarPerfMarker[action]();
    }
  };

  updateAdGroup = ({ adGroup, patch, converter = null }) => WithBulkUpdateRepository({
    patch: converter ? converter(patch) : patch,
    scope: this.props.scope,
    edmCampaign,
    updateLevel: 'AdGroup',
  }).then(() => {
    let newAdGroup = adGroup;

    if (patch.SearchBid || patch.CpmBid || patch.CpvBid) { // only one column will have value in one data row
      newAdGroup.bid = patch.SearchBid || patch.CpmBid || patch.CpvBid;
    } else {
      newAdGroup = defaults({}, patch, adGroup);
    }
    this.previousAdGroup = newAdGroup;

    // update finished means no error
    // show update result in advance for summary bar performance
    this.setState({
      patchAdGroup: newAdGroup,
    });
  });

  updateCampaign = ({
    campaign, patch, converter, ...others
  }) => WithBulkUpdateRepository({
    patch: converter ? converter(patch) : patch,
    scope: this.props.scope,
    edmCampaign,
    updateLevel: 'Campaign',
    ...others,
  }).then(() => {
    const { updateLevel: finalUpdateLevel } = others;
    const newCampaign = _.merge({}, campaign, finalUpdateLevel === 'Budget' ? { budget: patch } : patch);
    this.previousCampaign = newCampaign;

    // update finished means no error
    // show update result in advance for summary bar performance
    this.setState({
      patchCampaign: newCampaign,
    });
  });

  updateExperimentCampaign = ({
    campaign, patch, converter,
  }) => WithPatchExperimentRepository({
    patch: converter ? converter(patch) : patch,
    scope: this.props.scope,
    edmCampaign,
    experimentId: campaign.experimentId,
  });

  consumeOdataResults = RenderSummaryBar => ({
    adGroupFromOData,
    campaignFromOData,
    osData,
  }) => {
    this.markAction('willRender');

    const campaign = campaignFromOData
      ? convertToSummaryBarCampaign(campaignFromOData)
      : null;

    const adGroup = adGroupFromOData
      ? convertToSummaryBarAdGroup({
        ...adGroupFromOData,
        Status: adGroupFromOData.EntityStatus,
        Campaign: campaignFromOData,
      })
      : null;

    if (campaignFromOData && campaignFromOData.ExperimentId) {
      const experimentId = campaignFromOData.ExperimentId;
      const extendedCampaign = { experimentId, ...campaign };

      return (<WithExperimentRepository
        campaignLoadFallback={this.getCampaignLoadFallback}
        campaign={extendedCampaign}
        adGroup={adGroup}
        experimentId={experimentId}
        i18n={this.props.i18n}
        WrappedComponent={RenderSummaryBar}
      />);
    }

    return (<RenderSummaryBar campaign={campaign} adGroup={adGroup} osData={osData} />);
  };

  consumeAdGroupRepository = SummaryBarWithAdGroupCriterionResults => passThroughProps => (
    (
      <WithAdGroupRepository
        campaignLoadFallback={this.getCampaignLoadFallback}
        WrappedComponent={SummaryBarWithAdGroupCriterionResults}
        i18n={this.props.i18n}
        {...passThroughProps}
      />
    )
  );

  consumeCampaignRepository = SummaryBarWithCampaignCriterionResults => (
    (
      <WithCampaignRepository
        campaignLoadFallback={this.getCampaignLoadFallback}
        WrappedComponent={SummaryBarWithCampaignCriterionResults}
        i18n={this.props.i18n}
      />
    )
  );

  consumeOptimizationScoreRepository = SummaryBarWithCampaignAdGroupCriterionResults => passThroughProps => (
    (
      <WithOptimizationScoreRepository
        scope={this.props.scope}
        WrappedComponent={SummaryBarWithCampaignAdGroupCriterionResults}
        {...passThroughProps}
      />
    )
  );

  renderSummaryBar = () => ({
    experimentFromOData,
    campaign,
    adGroup: summaryBarAdGroup,
    osData,
  }) => {
    const props = this.getFinalProps;
    const summaryBarCampaign = experimentFromOData ?
      convertToSummaryBarExperimentCampaign(experimentFromOData, campaign)
      : campaign;
    const omitCampaignType = item => omit(item, 'campaignType');
    const willUpdate = (item, comparableItem) => item && !_.isEqual(omitCampaignType(comparableItem), omitCampaignType(item));
    const didUpdate = (item, comparableItem) => item && _.isEqual(omitCampaignType(comparableItem), omitCampaignType(item));

    const { patchAdGroup, patchCampaign } = this.state;
    const willAdGroupUpdated = willUpdate(patchAdGroup, summaryBarAdGroup);
    const willCampaignUpdated = willUpdate(patchCampaign, summaryBarCampaign);
    const didAdGroupUpdated = didUpdate(patchAdGroup, summaryBarAdGroup);
    const didCampaignUpdated = didUpdate(patchCampaign, summaryBarCampaign);

    // reset patchAdGroup
    if (didAdGroupUpdated && patchAdGroup) {
      this.setState({
        patchAdGroup: null,
      });
    }

    if (didCampaignUpdated && patchCampaign) {
      this.setState({
        patchCampaign: null,
      });
    }

    const finalCampaign = willCampaignUpdated ? patchCampaign : summaryBarCampaign;
    const finalAdGroup = willAdGroupUpdated ? patchAdGroup : summaryBarAdGroup;

    this.previousCampaign = finalCampaign;
    this.previousAdGroup = finalAdGroup;

    return (
      <SummaryBar
        {...props}
        campaign={finalCampaign}
        adGroup={finalAdGroup}
        osData={osData}
        perfMarker={this.campaignSummarybarPerfMarker}
        summaryBarActivityLogger={this.summaryBarActivityLogger}
      />
    );
  };

  render() {
    this.markAction('willFetchData');

    const { permissions } = this.props;
    const isOptimizationScoreOn = permissions.dynamic.OptimizationScore;

    const renderSummaryBarWithData = isOptimizationScoreOn ? compose(
      this.consumeCampaignRepository,
      this.consumeAdGroupRepository,
      this.consumeOptimizationScoreRepository,
      this.consumeOdataResults,
      this.renderSummaryBar
    ) : compose(
      this.consumeCampaignRepository,
      this.consumeAdGroupRepository,
      this.consumeOdataResults,
      this.renderSummaryBar
    );

    return renderSummaryBarWithData();
  }
}

UpdatableSummaryBar.propTypes = {
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  siteMap: siteMapPropType.isRequired,
  permissions: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  appPerfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }),
  instrumentation: PropTypes.shape({
    summaryBarScenario: PropTypes.any,
  }).isRequired,
};

UpdatableSummaryBar.defaultProps = {
  appPerfMarker: null,
};

export const SummaryBarWithScope
  = withInstrumentation(withSiteMap(withGlobalFilter(withScope(withI18nConsumer(UpdatableSummaryBar)))));
